/**
 * Persona Factory
 *
 * Central registry for all persona configurations.
 * Returns the appropriate persona config based on persona ID.
 *
 * Usage:
 *   import { getPersona, hasFeature } from '../personas';
 *
 *   const persona = getPersona(personaId);
 *   if (persona.hasFeature('mcqQuiz')) { ... }
 */

import basePersona, { createPersona } from './basePersona';
import entriPersona from './entri';
import qatarPersona from './qatar';
import evolutionPersona from './evolution';
import fiveGPersona from './5G';
import qatarHistoryPersona from './qatar_history';
import microwavePersona from './microwave';
import dewaPersona from './dewa';
// Registry of all known personas
const personaRegistry = {
  // Entri onboarding
  'p54ceeb77022': entriPersona,

  // Qatar conversational
  'pf5e3d8bef4a': qatarPersona,

  // Evolution learning
  'p99b6eb28083': evolutionPersona,

  'p607dc212fc9': fiveGPersona,
  'p3e5e7b16b65': qatarHistoryPersona,
  'p491e1e3a3d2': microwavePersona,
  'p0c7ef0b7ce0': dewaPersona,
};

/**
 * Get persona configuration by ID
 *
 * @param {string} personaId - The persona ID
 * @returns {object} Persona configuration (defaults to base if not found)
 */
export const getPersona = (personaId) => {
  if (!personaId) {
    console.warn('[Persona] No persona ID provided, using default');
    return createPersona({ id: null, name: 'Default' });
  }

  const persona = personaRegistry[personaId];

  if (!persona) {
    console.warn(`[Persona] Unknown persona ID: ${personaId}, using default`);
    return createPersona({ id: personaId, name: 'Unknown' });
  }

  return persona;
};

/**
 * Check if a persona has a specific feature enabled
 *
 * @param {string} personaId - The persona ID
 * @param {string} featureName - The feature to check
 * @returns {boolean} True if feature is enabled
 */
export const hasFeature = (personaId, featureName) => {
  const persona = getPersona(personaId);
  return persona.hasFeature(featureName);
};

/**
 * Get all registered persona IDs
 *
 * @returns {string[]} Array of persona IDs
 */
export const getAllPersonaIds = () => {
  return Object.keys(personaRegistry);
};

/**
 * Check if a persona ID is registered
 *
 * @param {string} personaId - The persona ID
 * @returns {boolean} True if persona is registered
 */
export const isRegisteredPersona = (personaId) => {
  return personaId in personaRegistry;
};

// Export individual personas for direct access if needed
export { entriPersona, qatarPersona, evolutionPersona, basePersona };

const personaApi = {
  getPersona,
  hasFeature,
  getAllPersonaIds,
  isRegisteredPersona,
};

export default personaApi;
